/*
 *  (c) 2015 - 2024 ENISCO by FORCAM GmbH
 */

import React from 'react'
import {Button, Dialog, DialogActions, Typography} from '@mui/material'
import {clone, TranslatedText, useError, useResolver, useTest} from '@enisco/mcc-react-lib'
import {FormattedMessage} from 'react-intl'
import {Favorite} from '../../../../../model/favorites'
import useFavoriteId from '../../../../../hooks/useFavoriteId'
import {useDispatch} from 'react-redux'
import {setCurrentApp} from '../../../../../store/appsSlice'
import {useNavigate} from 'react-router-dom'
import useLoadProfile from '../../../../../hooks/useLoadProfile'
import {pathToHome} from '../../../../../util/navigation'

interface Props {
  open: boolean
  onClose: () => void
  favorite: Favorite
}

const DeleteFavoriteDialog: React.FC<Props> = ({open, onClose, favorite}) => {
  const dispatch = useDispatch()
  const resolver = useResolver()
  const test = useTest()
  const error = useError()
  const [favoriteId] = useFavoriteId()
  const navigate = useNavigate()
  const [loadProfile] = useLoadProfile()

  const handleClose = () => {
    onClose()
  }

  const mockedData = {
    id: '1234abc',
    app: {app: 'app', module: 'module', hasAccess: true},
    user: 'testUser',
    groupId: 'abc1234'
  }

  const deleteFavorite = () => {
    const favoriteToDelete = clone(favorite)
    favoriteToDelete.deleteGroupIfEmpty = true
    resolver('mcc-portal-api', '/internal/favorites/' + favorite.id, {
      options: {
        method: 'DELETE',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(favoriteToDelete)
      },
      mockedResponse: test ? JSON.stringify(mockedData) : undefined
    })
      .then(() => {
        handleClose()
        if (favoriteId && favoriteId === favorite.id) {
          dispatch(setCurrentApp(undefined))
          navigate({pathname: pathToHome()})
        }
        return loadProfile()
      })
      .catch(error)
  }

  return (
    <Dialog
      open={open}
      PaperProps={{
        sx: {
          flexGrow: 1,
          paddingTop: 3,
          paddingLeft: 3,
          paddingRight: 3
        }
      }}
      data-testid={'DeleteFavoriteDialog:root'}
    >
      <Typography variant='h5'>
        <FormattedMessage
          id='FavoriteOptions.DeleteFavorite'
          defaultMessage='Delete favorite'
        />
      </Typography>
      <Typography variant='body1' sx={{paddingTop: 5}}>
        <FormattedMessage
          id='FavoriteOptions.AskDeleteFavorite'
          defaultMessage='Do you really want to delete the favorite {name}?'
          values={{
            name: <TranslatedText text={favorite.description} />
          }}
        />
      </Typography>
      <DialogActions sx={{paddingTop: 5}}>
        <Button data-testid='close-delete-dialog' onClick={handleClose}>
          <FormattedMessage
            id='FavoriteDialog.Cancel'
            defaultMessage='Cancel'
          />
        </Button>
        <Button onClick={deleteFavorite}>
          <FormattedMessage id='FavoriteDialog.Ok' defaultMessage='Ok' />
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DeleteFavoriteDialog
